import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS, waLink } from "../data/siteConfig";
import { WhatsAppIcon } from "./WhatsAppIcon";

const IstanbulSection = () => {
  const { t, isRtl } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  const eyebrowClass = isRtl
    ? "text-sm font-bold text-bronze-light"
    : "text-xs font-semibold uppercase tracking-[0.35em] text-bronze-light";

  return (
    <section
      id="istanbul"
      ref={ref}
      data-testid="istanbul-section"
      className="relative h-[85vh] min-h-[560px] overflow-hidden"
    >
      <motion.img
        src={BUSINESS.images.istanbul.src}
        alt={isRtl ? BUSINESS.images.istanbul.altAr : BUSINESS.images.istanbul.altEn}
        style={{ y }}
        loading="lazy"
        className="absolute inset-0 h-[125%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-ink/55" aria-hidden="true" />

      <div className="absolute inset-x-0 bottom-0 pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`mb-6 flex items-center gap-3 ${eyebrowClass}`}
          >
            <span className="h-px w-10 bg-bronze-light" aria-hidden="true" />
            {t.istanbul.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`max-w-3xl font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ivory ${
              isRtl ? "" : "tracking-tight"
            }`}
          >
            {t.istanbul.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-ivory/80"
          >
            {t.istanbul.copy}
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            href={waLink(t.istanbul.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="istanbul-cta"
            className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-bronze hover:text-white"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {t.istanbul.cta}
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default IstanbulSection;
